// public/v3/src/lib/bhi-assessment-entry.js — pure builder for
// BhiAssessmentModal save handler.
//
// Takes the modal's form state and produces two records to append in a
// single PUT:
//   1. assessmentRecord — structured entry for data.bhi.assessments[]
//   2. activityLogEntry — entry for data.activityLog[] tagged with the
//      patient's billing.bhiProgram CPT so entryProgramFamily()
//      classifies it as BHI.
//
// Also exports severityCssToken — maps computeSeverity() output to a CSS
// token. Shared between the modal and the assessment chip on Time Logs
// row so both surfaces use the same color mapping.
//
// Pure functions only. CommonJS so Jest can read directly.
//
// See spec §6.3 for the field-by-field layout.

function staffNameForUser(user) {
  return String((user && (user.displayName || user.name || user.email)) || '').trim();
}

function formatAssessmentDesc(form) {
  const sev = form.severity ? ' (' + form.severity + ')' : '';
  const noteRaw = String(form.notes || '').trim();
  const note = noteRaw ? ' ' + noteRaw.slice(0, 160) : '';
  return form.scale + ' administered, score ' + form.score + sev + '.' + note;
}

// Map a computeSeverity() output to a CSS token for color-coding the
// severity badge in BhiAssessmentModal and the assessment chip on Time Logs
// row. Shared helper so both surfaces use the same mapping. Custom-scale
// severities outside the built-in vocabulary fall to a neutral token.
function severityCssToken(severity) {
  const s = String(severity || '').toLowerCase();
  if (['minimal', 'low risk', 'no problems', 'below threshold', 'negative screen'].includes(s)) return 'sev-ok';
  if (['mild', 'at risk', 'low'].includes(s)) return 'sev-accent';
  if (s === 'moderate') return 'sev-amber';
  if (['moderately severe', 'substantial', 'probable ptsd', 'positive screen'].includes(s)) return 'sev-orange';
  if (['severe', 'high risk'].includes(s)) return 'sev-bad';
  return 'sev-neutral';
}

function buildAssessmentEntry(patient, form, currentUser, tsArg) {
  const ts = tsArg != null ? tsArg : Date.now();
  const rand = Math.random().toString(36).slice(2, 6);
  const id = 'r' + ts + rand;
  const staff = staffNameForUser(currentUser);
  const bhiProgram = patient && patient.billing && patient.billing.bhiProgram;

  const assessmentRecord = {
    scale: form.scale,
    date: form.date,
    score: form.score,
    severity: form.severity || '',
    notes: String(form.notes || '').trim(),
    recordedBy: staff || '',
    recordedByCredential: (currentUser && currentUser.credential) || '',
    ts,
  };

  const activityLogEntry = {
    id,
    pid: patient.id,
    type: 'BHI Assessment',
    duration: Number(form.timeSpent) || 5,
    ts,
    date: form.date,
    desc: formatAssessmentDesc(form),
    notes: String(form.notes || '').trim(),
    staff,
    staffUserId: (currentUser && currentUser.id) || '',
    kind: 'bhi-assessment',
    assessmentRef: {
      scale: form.scale,
      score: form.score,
      severity: form.severity || '',
      date: form.date,
    },
  };
  if (bhiProgram) activityLogEntry.program = bhiProgram;

  return { assessmentRecord, activityLogEntry };
}

module.exports = { buildAssessmentEntry, formatAssessmentDesc, severityCssToken };
